import { useEffect, useState } from "react";

import { ConnectionState } from "@sendbird/chat";

import {
  MessagesAnalyticsEvent,
  MessagesAnalyticsScreen,
} from "@/constants/analytics/Analytics";
import { IndividualSupportChannelPrefix } from "@/constants/GroupChannels";
import { MessagesTabSubDeepLinks } from "@/constants/Messages";
import { ScreenPath, TabPath } from "@/constants/Navigation";
import { DeepLinkParams } from "@/constants/Params";
import { useLocalSearchParams } from "@/crossPlatform/expo-router";
import { useSendbirdChat } from "@/crossPlatform/Sendbird";
import useSubDeepLink from "@/hooks/deepLinking/useSubDeepLink";
import useChatChannelContext from "@/hooks/useChatChannels";
import useEncodedNavigation from "@/hooks/useEncodedNavigation";
import { getUserTreatmentPhase } from "@/hooks/usePatientStatus";
import useAppMonitor from "@/services/AppMonitor";
import {
  useAppUserLinkedPatientsSelector,
  useAppUserSelector,
} from "@/store/reducers/appUser";
import { getChannelDisplayName } from "@/utils/groupChannel.util";
import { toAbsolutePath } from "@/utils/navigation.util";

/**
 * Handles deep links to individual message channels.
 *
 * When a deep link like `/messages/:channelUrl` is opened,
 * this hook fetches the channel via the Sendbird SDK and navigates to the channel screen.
 *
 * Permissions are handled by Sendbird - if the user doesn't have access to the channel,
 * the SDK's getChannel() will fail and we track an error.
 */
const useMessageChannelSubDeepLink = () => {
  const { sdk } = useSendbirdChat();
  const { navigateWithEncoding } = useEncodedNavigation();
  const { captureException } = useAppMonitor();
  const { externalId: appUserExternalId, isPatient: isAppUserPatient } =
    useAppUserSelector();
  const proxyPatients = useAppUserLinkedPatientsSelector();
  const {
    individualSupportChannels,
    selectedPatientId,
    selectedPatientName,
    selectedPatientProfile,
    setSelectedPatientId,
  } = useChatChannelContext();

  const userTreatmentPhase = getUserTreatmentPhase(
    selectedPatientProfile?.universalStatusCode,
  );

  const { [DeepLinkParams.Channel]: channelPrefix, id } = useLocalSearchParams<{
    [DeepLinkParams.Channel]?: string;
    id?: string;
    target?: string;
  }>();
  const [channelUrl, setChannelUrl] = useState<string>("");
  const isConnected = sdk.connectionState === ConnectionState.OPEN;

  useEffect(() => {
    if (!channelPrefix) return;
    // if the user is a patient, their channel URL ends in their externalId
    if (isAppUserPatient) {
      setChannelUrl(`${channelPrefix}_${appUserExternalId}`);
      return;
    }
    // if the user is a support, all their individual support channels begin in "SUP" and end in a unique id
    const isIndividualSupportChannel = channelPrefix.startsWith(
      IndividualSupportChannelPrefix,
    );
    // support users also can have channels including a linked patient,
    // but these are currently unsupported by deep linking,
    // as they are uniquely identified by the patientId, which is PII
    if (!isIndividualSupportChannel || !id) return;
    const supportChannel = individualSupportChannels.find((channel) =>
      channel.url.includes(id),
    );

    if (!supportChannel) return;
    setChannelUrl(supportChannel.url);
  }, [
    channelPrefix,
    appUserExternalId,
    isAppUserPatient,
    id,
    individualSupportChannels,
  ]);

  const navigateToChannel = ({
    channelDisplayName,
    channelPatientId,
  }: {
    channelDisplayName: string;
    channelPatientId?: string;
  }) => {
    const channelParams = {
      channelDisplayName,
      channelUrl,
      userTreatmentPhase,
    };

    if (channelPatientId && channelPatientId !== selectedPatientId) {
      setSelectedPatientId(channelPatientId);
    }

    navigateWithEncoding({
      params: channelParams,
      pathname: toAbsolutePath([TabPath.Messages, ScreenPath.GroupChannel]),
    });
  };

  const handleOpenChannel = async () => {
    if (!isConnected) {
      captureException(
        new Error("Message channel deep link fired before Sendbird connected"),
      );
      return;
    }

    try {
      const channel = await sdk.groupChannel.getChannel(channelUrl);

      // extract patientId from channel metadata for proxy user auto-selection
      const channelMetadata: ChannelMetadata = channel.cachedMetaData;
      const channelPatientId = channelMetadata.patientId;

      const channelDisplayName = getChannelDisplayName({
        channel,
        isAppUserPatient,
        selectedPatientName,
      });

      navigateToChannel({ channelDisplayName, channelPatientId });
    } catch (error) {
      // fallback to select the first patient
      const firstPatientId = proxyPatients?.[0]?.externalId;
      if (firstPatientId && firstPatientId !== selectedPatientId) {
        setSelectedPatientId(firstPatientId);
      }
      captureException(
        new Error(`Failed to open message channel with URL: ${channelUrl}`, {
          cause: error,
        }),
      );
    }
  };

  useSubDeepLink({
    analyticsEvent: MessagesAnalyticsEvent.ChannelOpenedFromDeepLink,
    analyticsProps: {
      source: MessagesAnalyticsScreen.ChannelList,
    },
    deeplinkIdentifiers: [DeepLinkParams.Channel],
    onOpen: () => void handleOpenChannel(),
    shouldHandle: isConnected && Boolean(channelUrl),
    targetValue: MessagesTabSubDeepLinks.Channel,
  });

  return { channelUrl };
};

export default useMessageChannelSubDeepLink;
